import React from "react";
import useTableSearch from "../hooks/useTableSearch";

interface SearchBarProps {
  onSearch: (searchTerm: string) => void;
}

const SearchBar: React.FC<SearchBarProps> = ({ onSearch }) => {
  const {
    searchText,
    isDropdownOpen,
    isFocused,
    searchInputRef,
    setIsFocused,
    handleSearchInputChange,
    handleKeyPress,
    executeSearch,
    resetSearch,
  } = useTableSearch({ onSearch });

  return (
    <div className="relative max-w-3xl mx-auto">
      <div className="relative">
        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
          <svg
            className="w-5 h-5 text-gray-400"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <input
          type="text"
          ref={searchInputRef}
          className="block w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-16 text-gray-900 focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          placeholder="A75 or DSPC or Cholesterol or DMG-PEG2000 or lnp-008914"
          value={searchText}
          onChange={handleSearchInputChange}
          onKeyPress={handleKeyPress}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          {searchText && (
            <button
              onClick={resetSearch}
              type="button"
              className="mr-2 inline-flex items-center rounded-lg bg-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-300 focus:outline-none focus:ring-4 focus:ring-gray-100"
            >
              Reset
            </button>
          )}
          <button
            onClick={executeSearch}
            className="inline-flex items-center rounded-lg bg-blue-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-300"
            type="button"
          >
            Search
          </button>
        </div>
      </div>

      {(isFocused || isDropdownOpen) && (
        <div className="absolute z-10 w-full p-4 mt-2 bg-white border border-gray-300 rounded-lg shadow-lg">
          <h4 className="mb-3 text-sm font-medium text-gray-900">
            Search Examples:
          </h4>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <p className="text-xs font-medium text-gray-600">
                Include at least elements:
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-700 rounded-md bg-blue-50 ring-1 ring-inset ring-blue-700/10">
                  DSPC and Cholesterol
                </span>
                <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-700 rounded-md bg-blue-50 ring-1 ring-inset ring-blue-700/10">
                  DOPE and DMG-PEG2000
                </span>
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-xs font-medium text-gray-600">
                Include only elements:
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-700 rounded-md bg-blue-50 ring-1 ring-inset ring-blue-700/10">
                  DSPC
                </span>
                <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-700 rounded-md bg-blue-50 ring-1 ring-inset ring-blue-700/10">
                  A75_C6B1
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
